import React, { useEffect, useRef, useState } from 'react';

import JsSIP from 'jssip';
import { Map } from 'immutable';
import { RouteComponentProps } from 'react-router-dom';

import { getQueryParam } from 'flow/utils/browser-utils';
import Button from 'flow/components/ui-components/Button';

const inputClass =
    'focus-ring rounded border border-gray-300 px-3 py-1.5 text-sm';

const parseHeaders = (headerString: string | null): Map<string, string> => {
    let headerMap = Map<string, string>();
    if (headerString) {
        headerString.split(',').forEach((pair) => {
            const [key, value] = pair.split(':');
            headerMap = headerMap.set(key, value);
        });
    }
    return headerMap;
};

const getDecodedPassword = (encodedPassword: string | null): string => {
    if (encodedPassword) {
        return atob(encodedPassword);
    }
    return '';
};

const SipTest = ({ location }: RouteComponentProps) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [server, setServer] = useState('');
    const [numberToCall, setNumberToCall] = useState('');
    const [headerName, setHeaderName] = useState('');
    const [headerValue, setHeaderValue] = useState('');
    const [headers, setHeaders] = useState(Map<string, string>());
    const [session, setSession] = useState<any>(null);
    const [muted, setMuted] = useState(false);

    const userAgentRef = useRef<any>(null);
    const remoteAudioRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        setServer(getQueryParam(location.search, 'server') || '');
        setUsername(getQueryParam(location.search, 'username') || '');
        setPassword(
            getDecodedPassword(getQueryParam(location.search, 'password')),
        );
        setNumberToCall(getQueryParam(location.search, 'number') || '');
        setHeaders(parseHeaders(getQueryParam(location.search, 'headers')));
        JsSIP.debug.enable('JsSIP:*');
    }, []);

    const handleRegister = () => {
        const socket = new JsSIP.WebSocketInterface('wss://' + server);
        const configuration = {
            sockets: [socket],
            uri: `sip:${username}@${server}`,
            password,
        };
        if (userAgentRef.current) {
            userAgentRef.current.stop();
        }
        userAgentRef.current = new JsSIP.UA(configuration);
        userAgentRef.current.start();
        userAgentRef.current.on('newRTCSession', handleIncomingCall);
    };

    const handleRemoteStream = (e: any) => {
        if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = e.stream;
        }
    };

    const handleIncomingCall = (data: any) => {
        if (data.originator === 'local') return;

        const newSession = data.session;
        newSession.answer({
            mediaConstraints: { audio: true, video: false },
            pcConfig: { rtcpMuxPolicy: 'require' },
        });
        newSession.connection.onaddstream = handleRemoteStream;
        setSession(newSession);
    };

    const handleMakeCall = () => {
        const target = `sip:${numberToCall}@${server}`;
        const extraHeaders = headers
            .entrySeq()
            .map(([k, v]) => `${k}: ${v}`)
            .valueSeq()
            .toArray();

        const newSession = userAgentRef.current.call(target, {
            mediaConstraints: { audio: true, video: false },
            pcConfig: { rtcpMuxPolicy: 'require' },
            extraHeaders,
        });
        newSession.connection.onaddstream = handleRemoteStream;
        setSession(newSession);
    };

    const handleHangupCall = () => {
        session.terminate();
        setSession(null);
    };

    const handleMuteUnmute = () => {
        if (muted) {
            session.unmute({ audio: true, video: true });
            setMuted(false);
        } else {
            session.mute({ audio: true, video: true });
            setMuted(true);
        }
    };

    const addHeader = () => {
        setHeaders((prev) => prev.set(headerName, headerValue));
        setHeaderName('');
        setHeaderValue('');
    };

    const removeHeader = (key: string) => () => {
        setHeaders((prev) => prev.delete(key));
    };

    const getUrl = () => {
        let url = 'app.telavox.com/siptest?';
        if (server) url += `server=${server}&`;
        if (username) url += `username=${username}&`;
        if (password) url += `password=${btoa(password)}&`;
        if (numberToCall) url += `number=${numberToCall}&`;
        if (headers && !headers.isEmpty()) {
            url += `headers=${headers
                .entrySeq()
                .map(([k, v]) => `${k}:${v}`)
                .join()}`;
        }
        return url;
    };

    return (
        <div className="flex flex-col gap-4 p-6">
            <audio ref={remoteAudioRef} autoPlay />
            <h1 className="text-xl font-bold">{'Sip Test'}</h1>

            <form
                className="flex flex-col gap-2"
                onSubmit={(e) => e.preventDefault()}
            >
                <input
                    id="server"
                    className={inputClass}
                    placeholder="sipproxy1.telavox.se"
                    value={server}
                    onChange={(e) => setServer(e.target.value)}
                />
                <input
                    id="username"
                    className={inputClass}
                    placeholder="u0406200000"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                />
                <input
                    id="password"
                    className={inputClass}
                    placeholder="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                    id="registerButton"
                    color="primary"
                    onClick={handleRegister}
                >
                    {'Register'}
                </Button>
            </form>

            <form
                className="flex flex-col gap-2"
                onSubmit={(e) => e.preventDefault()}
            >
                <input
                    id="number"
                    className={inputClass}
                    placeholder="phone number"
                    value={numberToCall}
                    onChange={(e) => setNumberToCall(e.target.value)}
                />
                <div className="flex gap-2">
                    <input
                        id="headerName"
                        className={inputClass}
                        placeholder="Header name"
                        value={headerName}
                        onChange={(e) => setHeaderName(e.target.value)}
                    />
                    <input
                        id="headerValue"
                        className={inputClass}
                        placeholder="Header value"
                        value={headerValue}
                        onChange={(e) => setHeaderValue(e.target.value)}
                    />
                    <Button
                        id="addHeaderButton"
                        color="gray"
                        onClick={addHeader}
                    >
                        {'Add header'}
                    </Button>
                </div>
                <div className="flex flex-col gap-1">
                    {headers.entrySeq().map(([key, val]) => (
                        <div
                            key={key}
                            className="flex items-center gap-2 text-sm"
                        >
                            <span>{`${key} : ${val}`}</span>
                            <Button
                                id={`removeHeader-${key}`}
                                color="gray"
                                onClick={removeHeader(key)}
                            >
                                {'Remove header'}
                            </Button>
                        </div>
                    ))}
                </div>
                <Button
                    id="callButton"
                    color="primary"
                    onClick={handleMakeCall}
                >
                    {'Call'}
                </Button>
            </form>

            <div className="flex gap-2">
                <Button
                    id="hangupButton"
                    color="gray"
                    onClick={handleHangupCall}
                >
                    {'Hang up'}
                </Button>
                <Button id="muteButton" color="gray" onClick={handleMuteUnmute}>
                    {muted ? 'Unmute' : 'Mute'}
                </Button>
            </div>

            <p className="text-xs text-gray-500">{getUrl()}</p>
        </div>
    );
};

export default SipTest;
